import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import { Link } from 'react-router-dom';
import { AccountStructure } from '../../../models/accounts-structure';
import { Account } from '../../../models/account';
import PrintFieldValue from '../print-field-value/PrintFieldValue';
import { useLocation } from 'react-router-dom';
import { DocumentStructure } from '../../../models/document-structure';
import { Document } from '../../../models/document';
import { Item } from '../../../models/item';
import { Work } from '../../../models/work';
import { Car } from '../../../models/car';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from "@emotion/react";
import { getDocumentFieldPrefix } from '@shared/lib/documents';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import type { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';

interface Options {
    label: string;
    icon: JSX.Element;
    href: string
}

interface DataTablePaginationProps {
    page: number;
    perPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    onNextPage: () => void;
    onPrevPage: () => void;
    onRowsPerPageChange: (perPage: number) => void;
}

interface DataTableProps<T extends Account | Document | Item | Work | Car> {
    structure: AccountStructure[] | DocumentStructure[];
    data: T[];
    sortKey: string;
    sortMethod: string;
    isLoading: boolean;
    options: Options[];

    setSortKey: (sortKey: string) => void;
    setSortMethod: (method: string) => void;
    setStructure: () => void;

    selectRow?: boolean;
    onSelectRow?: (row: T) => void;
    /**
     * @future Optional row expansion support can be added with MUI Collapse rows.
     */
    pagination?: DataTablePaginationProps;


}

function CursorPaginationActions(props: TablePaginationActionsProps & Pick<DataTablePaginationProps, "hasNextPage" | "hasPrevPage">) {
    const { page, onPageChange, hasNextPage, hasPrevPage } = props;

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={(event) => onPageChange(event, page - 1)}
                disabled={!hasPrevPage}
                aria-label="previous page"
            >
                <KeyboardArrowLeftIcon />
            </IconButton>
            <IconButton
                onClick={(event) => onPageChange(event, page + 1)}
                disabled={!hasNextPage}
                aria-label="next page"
            >
                <KeyboardArrowRightIcon />
            </IconButton>
        </Box>
    );
}

function DataTable<T extends Account | Document | Item | Work | Car>({ structure, data, sortKey, setSortKey, sortMethod, setSortMethod, isLoading, options, setStructure, selectRow = false, onSelectRow, pagination }: DataTableProps<T>) {

    const location = useLocation();
    const theme = useTheme();
    const ITEM_HEIGHT = 48;

    const orderedStructure = [...structure].sort((a, b) => {
        if (a.location === null) return 1; // 'a' comes after 'b'
        if (b.location === null) return -1; // 'b' comes after 'a'
        return a.location - b.location; // Sort by location if both are not null
    });

    const [anchorElMap, setAnchorElMap] = useState<{ [key: number]: Element | null }>({});

    

    function handleClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, rowId: number) {
        setAnchorElMap({ ...anchorElMap, [rowId]: event.currentTarget });
    };


    function handleClose(rowId: number) {
        setAnchorElMap({ ...anchorElMap, [rowId]: null });
    };


    function thClicked(field: AccountStructure | DocumentStructure) {

        if (sortKey !== field.field_name) {
            setSortKey(field.field_name);
        }

        else {
            if (sortMethod === 'ASC') {
                setSortMethod('DESC');
            }
            else {
                setSortMethod('ASC');
            }
        }
    }

    function onSelect(row: T) {
        onSelectRow?.(row);
    }

    function handleTablePageChange(_event: React.MouseEvent<HTMLButtonElement> | null, page: number) {
        if (!pagination) {
            return;
        }

        if (page > pagination.page) {
            pagination.onNextPage();
            return;
        }

        if (page < pagination.page) {
            pagination.onPrevPage();
        }
    }

    return (
        <Box>
            <TableContainer
                sx={{
                    width: "100%",
                    backgroundColor: theme.colors.section_color,
                    borderRadius: "5px",
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                    {orderedStructure.map((field, index) => (
                            <TableCell key={index} sx={{ borderBottom: "1px solid rgb(21, 35, 43)" }}>
                                <TableSortLabel
                                    active={sortKey === field.field_name || sortKey === `documents_settings.${field.field_name}`}
                                    direction={sortMethod === "ASC" ? "asc" : "desc"}
                                    onClick={() => thClicked(field)}
                                >
                                    {field.field_display_name}
                                </TableSortLabel>
                            </TableCell>
                    ))}
                            <TableCell sx={{ borderBottom: "1px solid rgb(21, 35, 43)", textAlign: "end" }}>
                                <Box sx={{ display: "flex", justifyContent: "end" }}>
                                    <Tooltip title="הגדרת שדות">
                                        <IconButton onClick={setStructure} sx={{ color: theme.colors.main_color }}>
                                            <SettingsIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={structure.length + 1}>
                                    <CircularProgress color="inherit" sx={{ display: "block", margin: "auto", padding: 4 }} />
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((obj, objIndex) => {
                                const rowId = obj.id ?? objIndex;

                                return (
                                    <TableRow
                                        key={rowId}
                                        onClick={() => {
                                            onSelect(obj);
                                        }}
                                        sx={{ cursor: selectRow ? "pointer" : "default" }}
                                    >
                            {orderedStructure.map((field) => (
                                            <TableCell key={field.field_name} sx={{ borderBottom: "1px solid rgb(21, 35, 43)" }}>
                                                <PrintFieldValue field={field} prefix={getDocumentFieldPrefix(field)} accountData={obj} />
                                            </TableCell>
                            ))}
                                        <TableCell sx={{ borderBottom: "1px solid rgb(21, 35, 43)", textAlign: "end", whiteSpace: "nowrap" }}>
                                            {options && (
                                                <>
                                                    <IconButton
                                                        aria-label="more"
                                                        aria-controls={`long-menu-${rowId}`}
                                                        aria-haspopup="true"
                                                        onClick={(event) => handleClick(event, rowId)}
                                                        style={{ color: theme.colors.main_color }}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    <Menu
                                                        id={`long-menu-${rowId}`}
                                                        anchorEl={anchorElMap[rowId]}
                                                        open={Boolean(anchorElMap[rowId])}
                                                        onClose={() => handleClose(rowId)}
                                                        PaperProps={{
                                                            style: {
                                                                backgroundColor: theme.colors.section_color,
                                                                color: theme.colors.main_color,
                                                                maxHeight: ITEM_HEIGHT * 4.5,
                                                                width: '20ch',
                                                            },
                                                        }}
                                                    >
                                                        {options.map((option) => (
                                                            <MenuItem
                                                                component={Link}
                                                                to={`${location.pathname}/${obj.id}/${option.href}`}
                                                                key={option.label}
                                                            >
                                                                {option.icon}
                                                                <ListItemText>{option.label}</ListItemText>
                                                            </MenuItem>
                                                        ))}
                                                    </Menu>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {pagination && (
                <TablePagination
                    component="div"
                    count={-1}
                    page={pagination.page}
                    onPageChange={handleTablePageChange}
                    rowsPerPage={pagination.perPage}
                    onRowsPerPageChange={(event) => {
                        pagination.onRowsPerPageChange(Number(event.target.value));
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelDisplayedRows={({ from, to }) => `${from}-${to}`}
                    labelRowsPerPage="שורות בעמוד"
                    ActionsComponent={(props) => (
                        <CursorPaginationActions
                            {...props}
                            hasPrevPage={pagination.hasPrevPage}
                            hasNextPage={pagination.hasNextPage}
                        />
                    )}
                    sx={{
                        ".MuiTablePagination-toolbar, .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiSvgIcon-root": {
                            color: theme.colors.main_color,
                        },
                    }}
                />
            )}
        </Box>

    );
}

export default DataTable